import { useTranslation } from 'react-i18next';

import { ASSETS_BASE_URL } from '@/config';
import { Icon, Typography } from '@/components';
import { OfferDetails } from '../../types';
import { getBuyerTypeLabel } from '../../helpers';

export type BuyerTypeSectionProps = {
  readonly offer: OfferDetails;
};

export function BuyerTypeSection({ offer }: BuyerTypeSectionProps) {
  const { t } = useTranslation(['offers']);
  const buyerTypeLabel = getBuyerTypeLabel(offer?.buyerType, t);

  return (
    <section className="mb-6">
      <Typography as="h4" color="primary" size="title" weight="semibold">
        {t('Prospective Buyer Information')}
      </Typography>
      <Typography as="p" size="body-3" color="secondary" weight="normal" className="mt-1 italic">
        {t(
          'Here you will find the information about the prospective buyer, and whether is the final purchaser or a representative acting for a third party.'
        )}
      </Typography>

      <div className="mt-6">
        <Typography as="p" size="body-2" weight="semibold">
          {t('Role')}
        </Typography>
        <Typography as="span" size="body-2" color="primary">
          {buyerTypeLabel}
        </Typography>
      </div>

      <div className="mt-6">
        <Typography as="p" size="body-2" weight="semibold">
          {t('Final Buyer Information')}
        </Typography>
        <Typography as="span" size="body-2" color="primary" className="whitespace-pre-line">
          {offer?.buyerComments}
        </Typography>
      </div>

      <div className="mt-6">
        <Typography as="p" size="body-2" weight="semibold">
          {t('Corporate Presentation')}
        </Typography>
        {offer?.buyerPresentation ? (
          <Typography as="span" size="body-2">
            <a
              href={`${ASSETS_BASE_URL}/${offer.buyerPresentation}`}
              target="_blank"
              rel="noreferrer"
              className="text-primary-700"
            >
              {t('Press to see the document')}
            </a>
          </Typography>
        ) : (
          <Typography as="span" size="body-2" color="primary">
            {t('No document provided')}
          </Typography>
        )}
      </div>

      <div className="mt-6">
        <Typography as="p" size="body-2" weight="semibold">
          {t('Corporate website')}
        </Typography>
        {offer?.buyerWebsite ? (
          <Typography as="span" size="body-2">
            <div className="flex items-center">
              {offer?.buyerWebsite}{' '}
              {offer?.buyerWebsite && !offer.buyerWebsite.includes('http://') && (
                <a
                  href={offer.buyerWebsite.includes('https://') ? offer.buyerWebsite : `https://${offer.buyerWebsite}`}
                  target="_blank"
                  className="text-primary-700 ml-1"
                >
                  <Icon name="external-link" className="h-4 w-4" />
                </a>
              )}
            </div>
          </Typography>
        ) : (
          <Typography as="span" size="body-2" color="primary">
            {t('No info provided')}
          </Typography>
        )}
      </div>
    </section>
  );
}
